import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'
import { useDispatch } from 'react-redux'
import {
    authSevenNewsLoggedInActionThunk,
    authSevenNewsLoggedOutActionThunk,
    authSevenNewsLoggingInActionThunk,
    readAuthJwtToken,
    readCoralJwtToken,
} from './action-thunks'
import { validateSevenNewsCoralToken } from './helpers'

interface AuthenticationProviderContextValues {
    loginUser(email: string, password: string): Promise<void>
    logoutUser(): Promise<void>
    /** True once the initial token reload on mount has settled. */
    isAuthReady: boolean
}

const SevenNewsAuthenticationContext = createContext<
    AuthenticationProviderContextValues | undefined
>(undefined)

export function useSevenNewsAuthActions() {
    const values = useContext(SevenNewsAuthenticationContext)
    if (!values) {
        throw new Error(
            'Ensure <SevenNewsAuthenticationContext /> is rendered above the component using this hook',
        )
    }

    return values
}

const SevenNewsAuthenticationProvider = ({ children }: any) => {
    const dispatch = useDispatch()
    const [isAuthReady, setIsAuthReady] = useState(false)

    /*
    401 response: return false, remove the token.
    Network or unexpected failure: return undefined, preserve the token.
    204 response: return true, preserve the valid token.
    */
    const reloadUserLogin = useCallback(async () => {
        const authJwtToken = readAuthJwtToken()
        const coralJwtToken = readCoralJwtToken()
        const isCoralTokenValid = coralJwtToken
            ? await validateSevenNewsCoralToken(coralJwtToken)
            : undefined

        await dispatch(
            authSevenNewsLoggedInActionThunk(
                authJwtToken || '',
                isCoralTokenValid === false
                    ? undefined
                    : coralJwtToken || undefined,
            ),
        )
    }, [dispatch])

    const loginUser = async (email: string, password: string) => {
        await dispatch(authSevenNewsLoggingInActionThunk(email, password))
    }

    const logoutUser = async () => {
        await dispatch(authSevenNewsLoggedOutActionThunk())
    }

    useEffect(() => {
        // Check if the user is already logged in when the component mounts
        reloadUserLogin().finally(() => setIsAuthReady(true))
    }, [reloadUserLogin])

    const values = {
        loginUser,
        logoutUser,
        isAuthReady,
    }

    return (
        <SevenNewsAuthenticationContext.Provider value={values}>
            {children}
        </SevenNewsAuthenticationContext.Provider>
    )
}

export { SevenNewsAuthenticationContext, SevenNewsAuthenticationProvider }
